<template>
  <ul class="km__list--modules" v-show="activeAreaColorObject !== null">
    <ColorSelectionButton
        css_typeclass="area"
        :is_active="colorFieldActive === 'area'"
        :pattern_active="activeAreaColorObject?.hatPattern || false"
        :background_color_hexstring="hexWithPoundsign(activeAreaColorObject)"
        :label="activeAreaColorObject?.hatPattern ? $t('km_farblabel_flaeche') : getColorNameFunction(activeAreaColorObject)"
        @click="openAreaColor"
    />

    <ColorSelectionButton
        v-if="outlineForActiveAreaColorObject !== null"
        css_typeclass="outline"
        :is_active="colorFieldActive === 'outline'"
        :pattern_active="false"
        :background_color_hexstring="hexWithPoundsign(outlineForActiveAreaColorObject)"
        :label="getColorNameFunction(outlineForActiveAreaColorObject)"
        @click="openOutlineColor"
    />

    <ColorSelectionButton v-if="may_have_pattern(activeAreaColorObject?.colorCode)"
                          css_typeclass="pattern"
                          :is_active="colorFieldActive === 'pattern'"
                          :pattern_active="activeAreaColorObject?.hatPattern"
                          :pattern-button-backround-image-preview-image="patternButtonBackroundImagePreviewImage as string"
                          :label="$t('konf_patterns')"
                          @click="openMainPattern"
    />
  </ul>
</template>

<script lang="ts" setup>
import {ColorUtilsVue} from "@km/js/ColorUtilsVue";
import {Color} from "@k/js/colorObject";
import {computed, ComputedRef, PropType, ref, Ref, toRefs} from "vue";
import {Store, useStore} from "vuex";
import {AnimationWaitingForTargetFactory, KeyFrameTargetValues,} from "@km/animations/animationFactory";
import {getColorName, PatternPreviewImageHolder} from "@km/store/ColorDataGetterFromResourceStore";

import {EventVerteiler} from "@k/global/eventVerteiler";
import {PATTERN_EVENTNAMES} from "@k/js/pattern/patternFactory";
import {useI18n} from "vue-i18n";
import {setWatchToAnimateOnStatechange} from "@km/js/animater";
import ColorSelectionButton from "@km/components/color/ColorSelectionButton.vue";
import {displayYesNoDialog} from "@km/components/popups/ConfirmOrConfirmCancelDialog";
import {useModuleColorOpenStateStore} from "@km/store/frontendstate/useModuleColorOpenState";


class ModuleColorEditFunctions {
  private readonly currentColorLayer: Ref<number>;
  private readonly activeAreaColorObject: Ref<Color>;
  private readonly outlineForActiveAreaColorObject: Ref<Color>;
  private readonly emit: (eventname: string, data?: string | number) => void;
  private readonly patternDeleteMessage: string;

  public readonly colorFieldActive: Ref<string | null>;
  public readonly patternButtonBackroundImagePreviewImage: ComputedRef<string | null>

  public constructor(props, emit, patternDeleteMessage: string) {
    this.colorFieldActive = ref(null) as Ref<string | null>;
    this.patternDeleteMessage = patternDeleteMessage;
    const {currentColorLayer, activeAreaColorObject, outlineForActiveAreaColorObject} = toRefs(props);
    this.currentColorLayer = currentColorLayer;
    this.activeAreaColorObject = activeAreaColorObject;
    this.outlineForActiveAreaColorObject = outlineForActiveAreaColorObject;
    this.emit = emit;
    this.patternButtonBackroundImagePreviewImage = computed(() => {
      return (!this.activeAreaColorObject.value || !this.activeAreaColorObject.value.hatPattern) ?
          "" :
          PatternPreviewImageHolder.getPreviewImage(this.activeAreaColorObject.value.colorCodeNr).value
    });
  }

  private askIfShouldDeletePattern(): Promise<void> {
    return new Promise<void>(resolve => displayYesNoDialog("", this.patternDeleteMessage).then(resolve))
  }

  public areaColorButtonClicked() {
    if (this.activeAreaColorObject.value.hatPattern) {
      this.askIfShouldDeletePattern().then(() => {
        EventVerteiler.broadcast(PATTERN_EVENTNAMES.DELETED, {colorCodeNr: this.activeAreaColorObject.value.colorCodeNr})
        this.openAreaColorChoice();
      })
      return;
    }
    this.openAreaColorChoice();
  }

  private openAreaColorChoice() {
    this.colorFieldActive.value = "area";
    this.emit("setColorObjectToEdit", this.activeAreaColorObject.value.colorCodeToDisplay);
  }

  public outlineColorButtonClicked() {
    this.colorFieldActive.value = "outline";
    this.emit("setColorObjectToEdit", this.outlineForActiveAreaColorObject.value.colorCodeToDisplay);
  }

  public patternButtonClicked() {
    this.colorFieldActive.value = "pattern"
    this.emit(this.activeAreaColorObject.value?.hatPattern ? "openpatterncolors" : 'openpatterncategories');
  }

  public setWatchToAnimateOnStatechange(store: Store<any>) {
    const openStateStore = useModuleColorOpenStateStore();
    setWatchToAnimateOnStatechange(
        AnimationWaitingForTargetFactory.getUpDownAnimation(".km__modules__color--area-outline-pattern-select .km__list--modules", 0.4),
        new KeyFrameTargetValues(0, 150, 77),
        () => openStateStore.getAreaOutlinePatternButtonState
    )
  }
}

const props = defineProps<{
  activeAreaColorObject: Color|null;
  outlineForActiveAreaColorObject: Color|null;
  may_have_pattern: (designname: string) => boolean;
}>();
const emit = defineEmits(["setColorObjectToEdit", "openpatterncategories", "openpatterncolors"]);

const store = useStore();
const func = new ModuleColorEditFunctions(props, emit, useI18n().t("konf_confirmdelete_pattern"));
func.setWatchToAnimateOnStatechange(store);


const colorFieldActive = func.colorFieldActive;
const hexWithPoundsign = ColorUtilsVue.hexWithPoundsign;
const openAreaColor = () => func.areaColorButtonClicked();
const openOutlineColor = () => func.outlineColorButtonClicked();
const openMainPattern = () => func.patternButtonClicked();
const getColorNameFunction = (c: Color | null) => c === null ? "" : getColorName(store, c.palettenCluster, c.palette, c.colorValue);
const patternButtonBackroundImagePreviewImage = func.patternButtonBackroundImagePreviewImage;

</script>

<style scoped lang="scss">
.km__list--modules {
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding-top: 4px;
}
</style>
